import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

interface Referral {
  id: string;
  referrer_id: string;
  referred_id: string | null;
  referral_code: string;
  status: string;
  reward_points: number;
  created_at: string;
  completed_at: string | null;
}

const generateReferralCode = (userId: string): string => {
  const prefix = 'AURA';
  const shortId = userId.slice(0, 6).toUpperCase();
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `${prefix}${shortId}${random}`;
};

export const useReferrals = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalReferred: 0,
    completedReferrals: 0,
    pendingReferrals: 0,
    totalPointsEarned: 0,
  });

  const fetchReferrals = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Fetch user's referrals
    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .eq('referrer_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching referrals:', error);
    } else {
      const referralData = data as Referral[];
      setReferrals(referralData);
      
      // Calculate stats
      const completed = referralData.filter(r => r.status === 'completed' || r.status === 'rewarded');
      const pending = referralData.filter(r => r.status === 'pending');
      
      setStats({
        totalReferred: referralData.length,
        completedReferrals: completed.length,
        pendingReferrals: pending.length,
        totalPointsEarned: completed.reduce((sum, r) => sum + r.reward_points, 0),
      });
    }

    setLoading(false);
  }, [user]);

  // Generate or fetch referral code
  const initializeReferralCode = useCallback(async () => {
    if (!user) return;

    // Check if user already has a code in profile
    if (profile?.referral_code) {
      setReferralCode(profile.referral_code);
      return;
    }

    // Generate new code
    const newCode = generateReferralCode(user.id);
    
    const { error } = await supabase
      .from('profiles')
      .update({ referral_code: newCode })
      .eq('id', user.id);

    if (error) {
      console.error('Error setting referral code:', error);
      return;
    }

    setReferralCode(newCode);
    await refreshProfile();
  }, [user, profile, refreshProfile]);

  useEffect(() => {
    fetchReferrals();
    initializeReferralCode();
  }, [fetchReferrals, initializeReferralCode]);

  const getShareableLink = useCallback(() => {
    if (!referralCode) return null;
    const baseUrl = window.location.origin;
    return `${baseUrl}/auth?ref=${referralCode}`;
  }, [referralCode]);

  const copyReferralLink = useCallback(async () => {
    const link = getShareableLink();
    if (!link) {
      toast({
        title: 'Error',
        description: 'Referral link not available',
        variant: 'destructive',
      });
      return false;
    }

    try {
      await navigator.clipboard.writeText(link);
      toast({
        title: '✨ Link Copied!',
        description: 'Share it with friends to earn 100 Aura Points each!',
      });
      return true;
    } catch (error) {
      console.error('Error copying link:', error);
      toast({
        title: 'Error',
        description: 'Could not copy link',
        variant: 'destructive',
      });
      return false;
    }
  }, [getShareableLink, toast]);

  const shareReferralLink = useCallback(async () => {
    const link = getShareableLink();
    if (!link) return false;

    const shareData = {
      title: 'Join AURA - Your AI Stylist',
      text: 'I\'ve been using AURA for style advice and it\'s amazing! Join using my link and we both get 100 Aura Points! 🎨✨',
      url: link,
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast({
          title: '🎉 Shared!',
          description: 'Thanks for spreading the style love!',
        });
        return true;
      } else {
        // Fallback to copy
        return copyReferralLink();
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error);
        return copyReferralLink();
      }
      return false;
    }
  }, [getShareableLink, copyReferralLink, toast]);

  const validateReferralCode = useCallback(async (code: string): Promise<string | null> => {
    if (!code) return null;

    try {
      // Use secure edge function to validate referral code
      const { data, error } = await supabase.functions.invoke('validate-referral', {
        body: { code }
      });

      if (error || !data?.valid) {
        return null;
      }

      return data.referrer_id;
    } catch (error) {
      console.error('Error validating referral code:', error);
      return null;
    }
  }, []);

  const applyReferral = useCallback(async (referrerUserId: string) => {
    if (!user || user.id === referrerUserId) return false;

    // Check if already referred
    if (profile?.referred_by) {
      return false;
    }

    // Update the referred user's profile
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ referred_by: referrerUserId })
      .eq('id', user.id);

    if (profileError) {
      console.error('Error applying referral:', profileError);
      return false;
    }

    // Create referral record
    const { error: referralError } = await supabase
      .from('referrals')
      .insert({
        referrer_id: referrerUserId,
        referred_id: user.id,
        referral_code: `REF-${user.id.slice(0, 8)}`,
        status: 'completed',
        completed_at: new Date().toISOString(),
      });

    if (referralError) {
      console.error('Error creating referral record:', referralError);
    }

    toast({
      title: '🎁 Referral Applied!',
      description: 'You and your friend will both receive 100 Aura Points!',
    });

    await refreshProfile();
    return true;
  }, [user, profile, toast, refreshProfile]);

  return {
    referrals,
    referralCode,
    loading,
    stats,
    getShareableLink,
    copyReferralLink,
    shareReferralLink,
    validateReferralCode,
    applyReferral,
    refresh: fetchReferrals,
  };
};
